/*!
 * bz-game-pools.js — item pools for The Focus Challenge
 * Session 32D · projects/briefs/focus-game-2026-08-04/tactics.md
 *
 * SIZING CORRECTION (2026-08-04): the QC panel estimated 300–500 LOCALISED items
 * (5 mechanics x 15–25 x 4 markets). That estimate does not hold for this mechanic set.
 * Four of the five mechanics carry NO language at all:
 *   reaction  → delay ranges          (no text)
 *   change    → colour grids          (no text)
 *   sequence  → tile flashes          (no text)
 *   maths     → generated digits      (no text)
 *   stroop    → colour/shape WORDS    ← the ONLY localised content
 * So the real localisation burden is the Stroop lexicon: ~4–8 words per market.
 * Everything else is procedural, which is also why no timed round depends on READING —
 * that is what removes the DE/FR reading-speed percentile bias the critic flagged.
 *
 * Pool sizes follow the item-banking guidance in the research: ~15–25 per slot with
 * no-repeat-until-exhausted gives 15–25 plays before any item recurs in that slot.
 */
(function (root) {
  'use strict';

  /* -------------------------------------------------- procedural pool builders */

  // REACTION — 20 items. Varying the delay window is the whole content model:
  // an unpredictable wait is what stops anticipation gaming the score.
  function reactionPool() {
    var out = [], i, lo, hi;
    for (i = 0; i < 20; i++) {
      lo = 800 + (i % 5) * 200;                 // 800–1600
      hi = lo + 1200 + Math.floor(i / 5) * 400; // spread 1.2s–2.4s above lo
      out.push({ id: 'rx' + (i + 1), minDelay: lo, maxDelay: hi });
    }
    return out;
  }

  // CHANGE BLINDNESS — 24 items across 4x4 and 5x5 grids, changed cell spread
  // across quadrants so the change is not habitually centre-weighted.
  function changePool() {
    var out = [], i, grid, n;
    for (i = 0; i < 24; i++) {
      grid = (i % 3 === 2) ? 5 : 4;
      n = grid * grid;
      out.push({
        id: 'cb' + (i + 1),
        grid: grid,
        // walk the index by a stride coprime with n so successive items land far apart
        changed: (i * 7 + 3) % n
      });
    }
    return out;
  }

  // STROOP — 18 items. Trial counts alternate so the congruent/incongruent split
  // always yields at least 3 of each (interference needs both means).
  function stroopPool() {
    var out = [], i;
    for (i = 0; i < 18; i++) {
      out.push({ id: 'st' + (i + 1), trials: (i % 3 === 0) ? 8 : 6 });
    }
    return out;
  }

  // SEQUENCE — 16 items. Start length 3 or 4; the round self-escalates from there,
  // so the item only sets the entry point.
  function sequencePool() {
    var out = [], i;
    for (i = 0; i < 16; i++) {
      out.push({ id: 'sq' + (i + 1), start: (i % 4 === 3) ? 4 : 3 });
    }
    return out;
  }

  // MATHS — 20 items. Difficulty deliberately capped: visitors arrive because they
  // struggle with focus, so this must never feel like a test they fail (research §2).
  function mathsPool() {
    var out = [], i, ops, max;
    for (i = 0; i < 20; i++) {
      if (i % 5 === 4)      { ops = ['x'];            max = 9;  }
      else if (i % 5 === 3) { ops = ['+', '-', 'x'];  max = 12; }
      else                  { ops = ['+', '-'];       max = 18 + (i % 3) * 6; }
      out.push({ id: 'mt' + (i + 1), ops: ops, max: max, trials: (i % 4 === 0) ? 6 : 5 });
    }
    return out;
  }

  // TIMING — 18 items. Anticipation timing (Calum 2026-08-05). Period and zone width set
  // difficulty; the round itself speeds up across its 3 trials.
  function timingPool() {
    var out = [], i;
    for (i = 0; i < 18; i++) {
      out.push({
        id: 'tm' + (i + 1),
        period: 1700 - (i % 6) * 110,      // 1700 → 1150 ms per sweep
        zoneHalf: (i % 3 === 2) ? 5 : 7,   // tighter zone every third item
        trials: 3
      });
    }
    return out;
  }


  /**
   * WORD LISTS — the DRM false-memory paradigm. Each list is a semantic cluster of 5 studied
   * words plus a CRITICAL LURE that fits the theme but is never shown. Most people confidently
   * "recognise" the lure; that is the finding.
   *
   * ⚠ THIS MECHANIC IS THE ONE THAT REINTRODUCES REAL LOCALISATION COST. Unlike the other six,
   * every word here is language-specific AND the semantic cluster must hold in that language —
   * a literal translation can break the lure effect if the translated lure is not the obvious
   * gist word. 6 lists x 4 markets = 24 lists / ~192 words today. Expanding to the 15–25-per-slot
   * guidance would be ~60 lists / ~480 words. DE/FR are unreviewed drafts and need a NATIVE
   * speaker to confirm each lure is genuinely the gist word for its cluster.
   */
  var WORD_LISTS = {
    uk: [
        { theme:'sleep', study:['BED','REST','TIRED','NIGHT','DREAM'],      lure:'SLEEP',  new:['TRAIN','GLASS'] },
        { theme:'cold',  study:['SNOW','WINTER','ICE','FROST','SHIVER'],    lure:'COLD',   new:['PAPER','CHAIR'] },
        { theme:'sweet', study:['SUGAR','HONEY','CANDY','CAKE','SYRUP'],    lure:'SWEET',  new:['STONE','RIVER'] },
        { theme:'music', study:['SONG','RADIO','BAND','TUNE','DRUM'],       lure:'MUSIC',  new:['CLOUD','BRICK'] },
        { theme:'fruit', study:['APPLE','PEAR','GRAPE','LEMON','PEACH'],    lure:'FRUIT',  new:['METAL','CLOCK'] },
        { theme:'sea',   study:['WAVE','BEACH','SAND','TIDE','SHELL'],      lure:'OCEAN',  new:['PENCIL','ENGINE'] }
      ],
    us: [
        { theme:'sleep', study:['BED','REST','TIRED','NIGHT','DREAM'],      lure:'SLEEP',  new:['TRAIN','GLASS'] },
        { theme:'cold',  study:['SNOW','WINTER','ICE','FROST','SHIVER'],    lure:'COLD',   new:['PAPER','CHAIR'] },
        { theme:'sweet', study:['SUGAR','HONEY','CANDY','CAKE','SYRUP'],    lure:'SWEET',  new:['STONE','RIVER'] },
        { theme:'music', study:['SONG','RADIO','BAND','TUNE','DRUM'],       lure:'MUSIC',  new:['CLOUD','BRICK'] },
        { theme:'fruit', study:['APPLE','PEAR','GRAPE','LEMON','PEACH'],    lure:'FRUIT',  new:['METAL','CLOCK'] },
        { theme:'sea',   study:['WAVE','BEACH','SAND','TIDE','SHELL'],      lure:'OCEAN',  new:['PENCIL','ENGINE'] }
      ],
    de: [
        { theme:'schlaf', study:['BETT','RUHE','MÜDE','NACHT','TRAUM'],        lure:'SCHLAF', new:['ZUG','GLAS'] },
        { theme:'kälte',  study:['SCHNEE','WINTER','EIS','FROST','ZITTERN'],   lure:'KALT',   new:['PAPIER','STUHL'] },
        { theme:'süß',    study:['ZUCKER','HONIG','KUCHEN','SIRUP','BONBON'],  lure:'SÜSS',   new:['STEIN','FLUSS'] },
        { theme:'musik',  study:['LIED','RADIO','BAND','MELODIE','TROMMEL'],   lure:'MUSIK',  new:['WOLKE','ZIEGEL'] },
        { theme:'obst',   study:['APFEL','BIRNE','TRAUBE','ZITRONE','PFIRSICH'], lure:'OBST', new:['METALL','UHR'] },
        { theme:'meer',   study:['WELLE','STRAND','SAND','FLUT','MUSCHEL'],    lure:'MEER',   new:['STIFT','MOTOR'] }
      ],
    fr: [
        { theme:'sommeil', study:['LIT','REPOS','FATIGUÉ','NUIT','RÊVE'],          lure:'SOMMEIL', new:['TRAIN','VERRE'] },
        { theme:'froid',   study:['NEIGE','HIVER','GLACE','GEL','FRISSON'],        lure:'FROID',   new:['PAPIER','CHAISE'] },
        { theme:'sucré',   study:['SUCRE','MIEL','GÂTEAU','SIROP','BONBON'],       lure:'SUCRÉ',   new:['PIERRE','RIVIÈRE'] },
        { theme:'musique', study:['CHANSON','RADIO','GROUPE','MÉLODIE','TAMBOUR'], lure:'MUSIQUE', new:['NUAGE','BRIQUE'] },
        { theme:'fruit',   study:['POMME','POIRE','RAISIN','CITRON','PÊCHE'],      lure:'FRUIT',   new:['MÉTAL','HORLOGE'] },
        { theme:'mer',     study:['VAGUE','PLAGE','SABLE','MARÉE','COQUILLE'],     lure:'MER',     new:['CRAYON','MOTEUR'] }
      ]
  };

  // WORDS — 18 items; listIndex selects which cluster (mod list count).
  function wordsPool() {
    var out = [], i;
    for (i = 0; i < 18; i++) out.push({ id: 'wd' + (i + 1), listIndex: i });
    return out;
  }

  // ODD ONE OUT — 20 items. Grid size only; no text, so nothing to localise.
  function oddOnePool() {
    var out = [], i;
    for (i = 0; i < 20; i++) out.push({ id: 'oo' + (i + 1), grid: (i % 3 === 0) ? 5 : 4 });
    return out;
  }

  // SUBITISE / SIMON / BISECT — all text-free, so nothing to localise in the items.
  function subitisePool() {
    var out = [], i;
    // Research standard for subitising exposure is ~100-250ms — deliberately short, because if
    // you can count it is not subitising. We already ran 2x that; testers still read it as too
    // short, which was the SURPRISE not the duration. Fix = a fixation beat before the flash
    // (bz-game.js) plus a longer exposure here, and +/-1 tolerance on the large sets.
    for (i = 0; i < 16; i++) out.push({ id: 'sb' + (i + 1), flashMs: 780 - (i % 4) * 60, trials: 4 });
    return out;
  }
  function simonPool() {
    var out = [], i;
    for (i = 0; i < 16; i++) out.push({ id: 'sm' + (i + 1), trials: (i % 3 === 0) ? 10 : 8 });
    return out;
  }
  function bisectPool() {
    var out = [], i;
    for (i = 0; i < 12; i++) out.push({ id: 'bs' + (i + 1), trials: 3 });
    return out;
  }

  // SWITCH / BLINK — no text in the items, so nothing to localise.
  function switchPool() {
    var out = [], i;
    for (i = 0; i < 14; i++) out.push({ id: 'sw' + (i + 1), trials: (i % 3 === 0) ? 12 : 10 });
    return out;
  }
  function blinkPool() {
    var out = [], i;
    for (i = 0; i < 12; i++) out.push({ id: 'bl' + (i + 1), itemMs: 120 - (i % 3) * 10, trials: 4 });
    return out;
  }

  /* ------------------------------------------------ localised content */
  /**
   * Stroop lexicons. Words must be SHORT and unambiguous in each language, because a
   * long word costs reading time and reading time is exactly what we must not measure.
   * German is the constraint: "Gelb"/"Grau"/"Grün"/"Blau" are all 4 letters — deliberate.
   * ⚠ DE/FR unreviewed drafts — native review required before ship.
   */
  var STROOP_LEXICON = {
    uk: {
      colours: [
        { key: 'blue',   hex: '#0A5FB8', label: 'BLUE' },
        { key: 'yellow', hex: '#E0A800', label: 'YELLOW' },
        { key: 'grey',   hex: '#6d6e71', label: 'GREY' },
        { key: 'green',  hex: '#1B7F4C', label: 'GREEN' }
      ],
      shapes: [
        { key: 'circle', label: 'CIRCLE', glyph: '●' },
        { key: 'square', label: 'SQUARE', glyph: '■' },
        { key: 'heart',  label: 'HEART',  glyph: '♥' },
        { key: 'star',   label: 'STAR',   glyph: '★' }
      ]
    },
    us: {
      colours: [
        { key: 'blue',   hex: '#0A5FB8', label: 'BLUE' },
        { key: 'yellow', hex: '#E0A800', label: 'YELLOW' },
        { key: 'grey',   hex: '#6d6e71', label: 'GRAY' },   // US spelling
        { key: 'green',  hex: '#1B7F4C', label: 'GREEN' }
      ],
      shapes: [
        { key: 'circle', label: 'CIRCLE', glyph: '●' },
        { key: 'square', label: 'SQUARE', glyph: '■' },
        { key: 'heart',  label: 'HEART',  glyph: '♥' },
        { key: 'star',   label: 'STAR',   glyph: '★' }
      ]
    },
    de: {
      colours: [
        { key: 'blue',   hex: '#0A5FB8', label: 'BLAU' },
        { key: 'yellow', hex: '#E0A800', label: 'GELB' },
        { key: 'grey',   hex: '#6d6e71', label: 'GRAU' },
        { key: 'green',  hex: '#1B7F4C', label: 'GRÜN' }
      ],
      shapes: [
        { key: 'circle', label: 'KREIS', glyph: '●' },
        { key: 'square', label: 'RAUTE', glyph: '◆' },
        { key: 'heart',  label: 'HERZ',  glyph: '♥' },
        { key: 'star',   label: 'STERN', glyph: '★' }
      ]
    },
    fr: {
      colours: [
        { key: 'blue',   hex: '#0A5FB8', label: 'BLEU' },
        { key: 'yellow', hex: '#E0A800', label: 'JAUNE' },
        { key: 'grey',   hex: '#6d6e71', label: 'GRIS' },
        { key: 'green',  hex: '#1B7F4C', label: 'VERT' }
      ],
      shapes: [
        { key: 'circle', label: 'CERCLE', glyph: '●' },
        { key: 'square', label: 'CARRÉ',  glyph: '■' },
        { key: 'heart',  label: 'CŒUR',   glyph: '♥' },
        { key: 'star',   label: 'ÉTOILE', glyph: '★' }
      ]
    }
  };

  function build(market) {
    var lex = STROOP_LEXICON[market];
    if (!lex) throw new Error('bz-game-pools: no Stroop lexicon for market "' + market + '"');
    return {
      pools: {
        reaction: reactionPool(),
        change:   changePool(),
        stroop:   stroopPool(),
        sequence: sequencePool(),
        maths:    mathsPool(),
        timing:   timingPool(),
        words:    wordsPool(),
        oddone:   oddOnePool(),
        subitise: subitisePool(),
        simon:    simonPool(),
        bisect:   bisectPool(),
        switch:   switchPool(),
        blink:    blinkPool()
      },
      lexicon: lex,
      wordLists: WORD_LISTS[market] || WORD_LISTS.uk
    };
  }

  /** Word length is a confound in a timed task — flag any lexicon that skews. */
  function lexiconReport() {
    var rows = [];
    Object.keys(STROOP_LEXICON).forEach(function (m) {
      ['colours', 'shapes'].forEach(function (kind) {
        var lens = STROOP_LEXICON[m][kind].map(function (w) { return w.label.length; });
        rows.push({
          market: m, kind: kind,
          min: Math.min.apply(null, lens),
          max: Math.max.apply(null, lens),
          spread: Math.max.apply(null, lens) - Math.min.apply(null, lens)
        });
      });
    });
    return rows;
  }

  root.BZGamePools = { build: build, STROOP_LEXICON: STROOP_LEXICON,
    WORD_LISTS: WORD_LISTS, lexiconReport: lexiconReport };
})(window);
